import {
  useLocalParticipant,
  useRemoteParticipants,
  useTrackTranscription,
  useVoiceAssistant,
} from '@livekit/components-react';
import { useMemo } from 'react';
import { useLocalMicTrack } from './useLocalMicTrack';
import { Track } from 'livekit-client';
import { PARTICIPANT_IDENTITIES } from '@widgets/Popup/TestingAgentPopup/constants/roomManager.constants';

export default function useCombinedTranscriptions() {
  const { agentTranscriptions } = useVoiceAssistant();
  const remoteParticipants = useRemoteParticipants();
  const localParticipant = useLocalParticipant();

  const micTrackRef = useLocalMicTrack();

  const customerParticipant = useMemo(() => {
    if (remoteParticipants.length === 0) {
      return null;
    }

    let customer = remoteParticipants.find((p) => {
      const identity = p.identity;

      return identity === PARTICIPANT_IDENTITIES.CUSTOMER;
    });

    if (!customer) {
      customer = remoteParticipants.find((p) => {
        const audioTrack = p.getTrackPublication(Track.Source.Microphone);
        return audioTrack && audioTrack.track;
      });
    }

    if (!customer && remoteParticipants.length > 0) {
      customer = remoteParticipants[0];
    }

    return customer;
  }, [remoteParticipants]);

  const remoteTrackRef = useMemo(() => {
    if (!customerParticipant) {
      return null;
    }

    const audioTrack = customerParticipant.getTrackPublication(Track.Source.Microphone);
    if (!audioTrack || !audioTrack.track) {
      return null;
    }

    return {
      participant: customerParticipant,
      source: Track.Source.Microphone,
      publication: audioTrack,
    };
  }, [customerParticipant]);

  const { segments: localUserTranscriptions } = useTrackTranscription(micTrackRef || undefined);
  const { segments: remoteUserTranscriptions } = useTrackTranscription(remoteTrackRef || undefined);

  const combinedTranscriptions = useMemo(() => {
    const isWebCall =
      localParticipant.localParticipant.identity === PARTICIPANT_IDENTITIES.CUSTOMER;

    const userTranscriptions = isWebCall ? localUserTranscriptions : remoteUserTranscriptions;

    return [
      ...agentTranscriptions.map((val) => {
        return { ...val, role: 'assistant' };
      }),
      ...userTranscriptions.map((val) => {
        return { ...val, role: 'user' };
      }),
    ].sort((a, b) => a.firstReceivedTime - b.firstReceivedTime);
  }, [
    agentTranscriptions,
    localUserTranscriptions,
    remoteUserTranscriptions,
    localParticipant.localParticipant.identity,
  ]);

  return combinedTranscriptions;
}
